import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Divider,
    Switch,
} from '@mui/material';

// Mercor-style toggle switch component
const MercorSwitch = ({ checked, onChange, disabled = false }) => (
    <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{
            width: 44,
            height: 24,
            padding: 0,
            '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: '2px',
                transitionDuration: '200ms',
                '&.Mui-checked': {
                    transform: 'translateX(20px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                        backgroundColor: '#4f46e5',
                        opacity: 1,
                        border: 0,
                    },
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 20,
                height: 20,
            },
            '& .MuiSwitch-track': {
                borderRadius: 12,
                backgroundColor: '#e5e7eb',
                opacity: 1,
            },
        }}
    />
);

const FormSection = ({ title, subtitle, children, sidebarWidth = '250px' }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 5 }, mb: 4, alignItems: 'flex-start' }}>
        <Box sx={{ width: { md: sidebarWidth }, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem', mt: 0.5 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
        <Box sx={{ flex: 1 }}>
            {children}
        </Box>
    </Box>
);

const CommunicationsTab = ({ userProfile, userId, authAxios, onUpdate, setError }) => {
    const [saving, setSaving] = useState(false);

    // Communication preferences matching API
    const [preferences, setPreferences] = useState({
        email_notifications_enabled: true,
        sms_notifications_enabled: false,
        fulltime_opportunities_enabled: true,
        parttime_opportunities_enabled: true,
        referral_opportunities_enabled: true,
        job_opportunities_notifications: true,
        work_updates_notifications: true,
        unsubscribe_from_all: false,
    });

    // Initialize form data from userProfile
    useEffect(() => {
        if (userProfile) {
            setPreferences({
                email_notifications_enabled: userProfile.email_notifications_enabled ?? true,
                sms_notifications_enabled: userProfile.sms_notifications_enabled ?? false,
                fulltime_opportunities_enabled: userProfile.fulltime_opportunities_enabled ?? true,
                parttime_opportunities_enabled: userProfile.parttime_opportunities_enabled ?? true,
                referral_opportunities_enabled: userProfile.referral_opportunities_enabled ?? true,
                job_opportunities_notifications: userProfile.job_opportunities_notifications ?? true,
                work_updates_notifications: userProfile.work_updates_notifications ?? true,
                unsubscribe_from_all: userProfile.unsubscribe_from_all ?? false,
            });
        }
    }, [userProfile]);

    // Handle preference toggle with mutual exclusivity logic
    const handlePreferenceToggle = (field, value) => {
        if (field === 'unsubscribe_from_all' && value === true) {
            // If enabling unsubscribe_from_all, turn off job_opportunities and work_updates
            setPreferences(prev => ({
                ...prev,
                [field]: value,
                job_opportunities_notifications: false,
                work_updates_notifications: false,
            }));
        } else if ((field === 'job_opportunities_notifications' || field === 'work_updates_notifications') && value === true) {
            // If enabling job_opportunities or work_updates, turn off unsubscribe_from_all
            setPreferences(prev => ({
                ...prev,
                [field]: value,
                unsubscribe_from_all: false,
            }));
        } else {
            setPreferences(prev => ({ ...prev, [field]: value }));
        }
    };

    // Save handler
    const handleSave = async () => {
        setSaving(true);
        try {
            await authAxios.patch('/self/', preferences);

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save communication preferences:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save communication preferences';
            if (setError) setError(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
            {/* Communication Channels Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Communication Channels
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Choose how you'd like to receive notifications
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection 
                    title="Email" 
                    subtitle="Receive notifications via email"
                >
                    <MercorSwitch
                        checked={preferences.email_notifications_enabled}
                        onChange={(value) => handlePreferenceToggle('email_notifications_enabled', value)}
                    />
                </FormSection>

                <FormSection 
                    title="SMS" 
                    subtitle="Receive notifications via text message"
                >
                    <MercorSwitch
                        checked={preferences.sms_notifications_enabled}
                        onChange={(value) => handlePreferenceToggle('sms_notifications_enabled', value)}
                    />
                </FormSection>
            </Box>

            {/* Opportunity Types Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Opportunity Types
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Get notified about specific types of opportunities
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection 
                    title="Full-time opportunities" 
                    subtitle="Contact me about full-time roles"
                >
                    <MercorSwitch
                        checked={preferences.fulltime_opportunities_enabled}
                        onChange={(value) => handlePreferenceToggle('fulltime_opportunities_enabled', value)}
                    />
                </FormSection>

                <FormSection 
                    title="Part-time opportunities" 
                    subtitle="Contact me about part-time roles"
                >
                    <MercorSwitch
                        checked={preferences.parttime_opportunities_enabled}
                        onChange={(value) => handlePreferenceToggle('parttime_opportunities_enabled', value)}
                    />
                </FormSection>

                <FormSection 
                    title="Referral opportunities" 
                    subtitle="Contact me about referral opportunities"
                >
                    <MercorSwitch
                        checked={preferences.referral_opportunities_enabled}
                        onChange={(value) => handlePreferenceToggle('referral_opportunities_enabled', value)}
                    />
                </FormSection>
            </Box>

            {/* General Notifications Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    General Notifications
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Control what types of notifications you receive
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection 
                    title="Job opportunities" 
                    subtitle="Notifications about new job openings, interviews, invitations"
                >
                    <MercorSwitch
                        checked={preferences.job_opportunities_notifications}
                        onChange={(value) => handlePreferenceToggle('job_opportunities_notifications', value)}
                    />
                </FormSection>

                <FormSection 
                    title="Work updates" 
                    subtitle="Updates about offers, trials, contracts, project status"
                >
                    <MercorSwitch
                        checked={preferences.work_updates_notifications}
                        onChange={(value) => handlePreferenceToggle('work_updates_notifications', value)}
                    />
                </FormSection>

                <FormSection 
                    title="Unsubscribe from all" 
                    subtitle="Turn this on to stop all outreach"
                >
                    <MercorSwitch
                        checked={preferences.unsubscribe_from_all}
                        onChange={(value) => handlePreferenceToggle('unsubscribe_from_all', value)}
                    />
                </FormSection>
            </Box>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        bgcolor: '#4f46e5',
                        textTransform: 'none',
                        px: 4,
                        py: 1,
                        borderRadius: '8px',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#4338ca' },
                    }}
                >
                    {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                </Button>
            </Box>
        </Box>
    );
};

export default CommunicationsTab;
